import { useEffect, useMemo, useRef, useState } from 'react';
import './OrderLifeCycle.scss';
import OrderLifeCycleBody from './components/OrderLifeCycleBody/OrderLifeCycleBody';
import OrderLifeCycleHeader from './components/OrderLifeCycleHeader/OrderLifeCycleHeader';
import OrderLifeCycleState from './components/OrderLifeCycleState/OrderLifeCycleState';
import { getUserInfo } from '../../helpers/localStorageHandler';
import { API_ENDPOINTS } from '../../assets/api/endpoints';
import axios from 'axios';
import dayjs from 'dayjs';
import { ActionType } from './types/ActionType';
import { MessageType } from '../../models/MessageType';
import { OrderStateType } from './types/OrderStateType';
import { OrderDetailsType } from './types/OrderDetailsType';
import { useLocation } from 'react-router-dom';
import { notification, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { OrderSource } from './types/OrderSource';
import { WalletDetails } from 'models/WalletDetails';
import { useResilientStompClient } from '../../hooks/useResilientStompClient';
import { useCountriesContext } from 'store/CountriesContext';
import { pushP2pDepositCompletedEventOnActionSuccess } from 'helpers/pushP2pDepositCompletedEvent';
import { fetchSocketJwtToken } from 'helpers/socketToken';
import { chatDebug, describeError, pageState } from 'helpers/chatDebugLog';

const DATE_TIME_FORMAT = 'MMM D, YYYY h:mm:ss A';
const ORDER_ACTION_TIMEOUT_MS = 30000;
const ORDER_DETAILS_TIMEOUT_MS = 15000;
// While the chat socket is down the counterparty's actions can't reach us as ACTION messages, so poll.
const ORDER_POLL_INTERVAL_MS = 10000;
const FINAL_ORDER_STATES: OrderStateType[] = ['COMPLETED', 'CANCELLED', 'REJECTED'];

function OrderLifeCycle() {
	const [notFound, setNotFound] = useState('');
	const [otpVlaid, setOtpValid] = useState({ value: false });
	const [orderDetails, setOrderDetails] = useState<OrderDetailsType | null>(null);
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const location = useLocation();
	const { countriesState } = useCountriesContext();

	const orderNumber = useMemo(() => {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get('id') ?? '';
	}, [location.search]);

	const isMerchant = useMemo(() => getUserInfo()?.isMerchant ?? false, []);
	// Authorization is added by the hook from a freshly read token on every connect attempt.
	const stompConnectHeaders = useMemo(() => ({ orderNumber }), [orderNumber]);
	const userName = useMemo(
		() => (isMerchant ? orderDetails?.clientDetails?.clientFullName || '' : orderDetails?.merchant?.fullName || ''),
		[isMerchant, orderDetails],
	);

	const {
		stompClient,
		reportDead: reportSocketDead,
		socketJwtToken,
	} = useResilientStompClient({
		url: API_ENDPOINTS.chat,
		getSocketJwtToken: fetchSocketJwtToken,
		headers: stompConnectHeaders,
		label: 'order-chat',
		onConnect: () => chatDebug.info('order', 'chat socket connected', { orderNumber }),
		onDisconnect: () => chatDebug.warn('order', 'chat socket disconnected, will keep retrying', { orderNumber }),
	});

	// Several refreshes can be in flight at once (action result, ACTION echo, reconnect catch-up). On a
	// slow connection they can resolve out of order, so only the most recently sent one may update state.
	const latestOrderDetailsRequest = useRef(0);

	const getOrderDetails = (reason = 'unspecified') => {
		const request = ++latestOrderDetailsRequest.current;
		const startedAt = Date.now();
		chatDebug.info('order', 'order details requested', { reason, request, orderNumber });
		return axios
			.get(
				API_ENDPOINTS.orderDetails
					.replace('%side%', isMerchant ? 'merchant' : 'client')
					.replace('%orderNumber%', orderNumber),
				{ timeout: ORDER_DETAILS_TIMEOUT_MS },
			)
			.then((response) => {
				const isLatest = request === latestOrderDetailsRequest.current;
				chatDebug.info('order', isLatest ? 'order details loaded' : 'order details ignored, a newer request exists', {
					reason,
					request,
					status: response.data?.status,
					actions: response.data?.actions,
					tookMs: Date.now() - startedAt,
				});
				if (isLatest) setOrderDetails(response.data);
			})
			.catch((err: any) => {
				chatDebug.error('order', 'order details failed', {
					reason,
					request,
					tookMs: Date.now() - startedAt,
					...describeError(err),
				});
				if (request === latestOrderDetailsRequest.current && err?.response?.status === 404) setNotFound(err);
			});
	};

	useEffect(() => {
		getOrderDetails('page load');
	}, [location.search, isMerchant, orderNumber]);

	// ACTION chat messages tell the counterparty to refresh the order and mark the chat as having had
	// an action. Actions are REST and work while the socket is down, so hold these until it is back.
	const pendingActionMessages = useRef<{ topic: string; body: string }[]>([]);
	const hasConnectedBefore = useRef(false);
	// Read in the action's response handler, which can run after the socket has been replaced.
	const stompClientRef = useRef(stompClient);
	stompClientRef.current = stompClient;

	useEffect(() => {
		pendingActionMessages.current = [];
		hasConnectedBefore.current = false;
	}, [orderNumber]);

	useEffect(() => {
		if (!stompClient?.connected) return;
		const pending = pendingActionMessages.current.splice(0);
		chatDebug.info('order', 'chat socket ready', {
			isReconnect: hasConnectedBefore.current,
			queuedActionMessagesSent: pending.length,
		});
		pending.forEach(({ topic, body }) => stompClient.send(topic, {}, body));
		// Anything the counterparty did while we were disconnected only reaches us through the order itself.
		if (hasConnectedBefore.current) getOrderDetails('chat reconnected');
		hasConnectedBefore.current = true;
	}, [stompClient]);

	const isSocketConnected = !!stompClient?.connected;
	const isOrderFinal = FINAL_ORDER_STATES.includes(orderDetails?.status as OrderStateType);
	useEffect(() => {
		if (isSocketConnected || isOrderFinal) return;
		let inFlight = false;
		const interval = setInterval(() => {
			if (inFlight || document.visibilityState !== 'visible') return;
			inFlight = true;
			getOrderDetails('poll while disconnected').finally(() => {
				inFlight = false;
			});
		}, ORDER_POLL_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [isSocketConnected, isOrderFinal, orderNumber]);

	const triggerAction = (action: ActionType, reasons?: string, otp?: string) => {
		setLoading(true);
		const payload = reasons ? { cancellationReason: reasons } : otp ? { otp } : {};
		const startedAt = Date.now();
		chatDebug.info('order', 'action started', {
			action,
			orderNumber,
			orderStatus: orderDetails?.status,
			chatConnected: !!stompClientRef.current?.connected,
			...pageState(),
		});
		axios
			.post(API_ENDPOINTS.orderAction.replace('%orderNumber%', orderNumber).replace('%action%', action), payload, {
				timeout: ORDER_ACTION_TIMEOUT_MS,
			})
			.then((response) => {
				chatDebug.info('order', 'action succeeded', {
					action,
					newStatus: response.data?.newStatus,
					tookMs: Date.now() - startedAt,
				});
				setOtpValid({ value: true });
				// Don't rely on the ACTION echo over the socket to refresh our own view; it may be down.
				getOrderDetails('action succeeded');

				if (orderDetails) {
					pushP2pDepositCompletedEventOnActionSuccess(
						orderDetails,
						isMerchant,
						action,
						countriesState.paymentMethods,
						response.data?.newStatus,
					);
				}

				const actionMessage = isMerchant
					? { merchantAction: action, type: MessageType.ACTION }
					: { clientAction: action, type: MessageType.ACTION };

				const topic = API_ENDPOINTS.sendOrderChatMsgTopic.replace('%orderNumber%', orderNumber);
				const body = JSON.stringify(actionMessage);
				const client = stompClientRef.current;
				if (client?.connected) {
					client.send(topic, {}, body);
					chatDebug.info('order', 'ACTION chat message sent', { action });
				} else {
					pendingActionMessages.current.push({ topic, body });
					chatDebug.warn('order', 'ACTION chat message queued until the chat reconnects', {
						action,
						queued: pendingActionMessages.current.length,
					});
				}
			})
			.catch((err: any) => {
				chatDebug.error('order', 'action failed', {
					action,
					tookMs: Date.now() - startedAt,
					noResponse: !err?.response,
					...describeError(err),
					...pageState(),
				});
				if (err?.response?.data.code === 2037 || err?.response?.data?.code === 2046) setOtpValid({ value: false });
				// No response means we don't know whether the action went through; the refresh below shows
				// the real state, so tell the user to check it rather than blindly retrying.
				if (!err?.response) {
					notification.error({ message: '', description: t<string>('actionNotConfirmed'), placement: 'bottom' });
				}
				getOrderDetails('action failed');
			})
			.finally(() => setLoading(false));
	};

	return (
		<div className='order-life-cycle-page'>
			{notFound ? (
				<Result status='404' title={t<string>('notFound')} />
			) : (
				<>
					<OrderLifeCycleHeader
						orderType={orderDetails?.type || 'BUY'}
						user={userName}
						orderNumber={orderNumber}
						openTime={dayjs(orderDetails?.createdAt).format(DATE_TIME_FORMAT)}
					/>
					<OrderLifeCycleState
						orderState={orderDetails?.status as OrderStateType}
						isMerchant={isMerchant}
						orderType={orderDetails?.type || 'BUY'}
						expireRemainingDuration={orderDetails?.expireRemainingDuration ?? 0}
						client={orderDetails?.clientDetails?.clientFullName ?? ''}
						merchant={orderDetails?.merchant?.fullName ?? ''}
						orderNo={orderNumber}
						getOrderDetails={getOrderDetails}
					/>
					<OrderLifeCycleBody
						loading={loading}
						orderNumber={orderNumber}
						isMerchant={isMerchant}
						orderState={orderDetails?.status as OrderStateType}
						userName={userName}
						note={orderDetails?.note || ''}
						orders={orderDetails?.merchant?.totalOrders || 0}
						reviews={orderDetails?.merchant?.totalReviews || 0}
						rating={orderDetails?.merchant?.rating || 0}
						feedback={orderDetails?.review ?? null}
						orderBody={{
							unitPrice: `${orderDetails?.price}`,
							quantity: `${orderDetails?.quantity?.toLocaleString()}`,
							amount: `${orderDetails?.amount?.toLocaleString()}`,
							paymentMehodId: orderDetails?.paymentMethod || 0,
							accountNumber: `${orderDetails?.accountDetails?.number}`,
							accountGroup: `${orderDetails?.accountDetails?.accountGroup}`,
						}}
						merchantDetails={orderDetails?.merchant}
						actions={orderDetails?.actions || []}
						orderType={orderDetails?.type || 'BUY'}
						merchantId={`${orderDetails?.merchant?.merchantId}` || ''}
						triggerAction={triggerAction}
						stompClient={stompClient}
						reportSocketDead={reportSocketDead}
						socketJwtToken={socketJwtToken}
						getOrderDetails={getOrderDetails}
						clientOrder={orderDetails?.clientDetails?.totalOrders ?? 0}
						clientCompletedOrders={orderDetails?.clientDetails?.ordersCompleted ?? 0}
						canReview={orderDetails?.canReview ?? false}
						paymentProof={orderDetails?.paymentProof ?? false}
						paymentProofFileId={orderDetails?.paymentProofFileId ?? ''}
						paymentProofFileLabel={orderDetails?.paymentProofFileLabel ?? ''}
						otpVlaid={otpVlaid}
						walletDetails={orderDetails?.walletDetails || ({} as WalletDetails)}
						source={orderDetails?.source || OrderSource.TRADING_ACCOUNT}
					/>
				</>
			)}
		</div>
	);
}

export default OrderLifeCycle;
